import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { StyledSnackbar } from "components/StyledComponents/styledComponents";
import { StyledAlert } from "components/StyledComponents/styledComponents";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Typography,
} from "@mui/material";
import {
  TableContainer,
  Paper,
  Grid,
  TextField,
  Box,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { CustomPaper } from "components/Styles/globalStyles";
import Cookie from "js-cookie";
import { subDialogContentStyle, dialogContentTextStyle } from "components/Styles/approvalStyles";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  inputStyle,
  StyledButton,
  StyledTypography,
  StyledAgGridContainer,
  StyledTableContainer,
} from "components/StyledComponents/styledComponents.js";
import { useMaterialUIController } from "context";
import URLs from "constants/urls";
import VisibilityIcon from "@mui/icons-material/Visibility";

export default function ApproveLeaves() {
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const [employeeData, setEmployeeData] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState("success");
  const [loading, setLoading] = useState(false);
  const [openApprovalDialog, setOpenApprovalDialog] = useState(false);
  const [approvalParams, setApprovalParams] = useState({});
  const [remarksDialogOpen, setRemarksDialogOpen] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState("");

  const navigate = useNavigate();
  const MInput = inputStyle();
  const hrms_token = Cookie.get("hrms_token");
  const showNotification = (message, type = "success") => {
    setSnackbarMessage(message);
    setSnackbarStatus(type);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const fetchLeaveData = async () => {
    setLoading(true);
    try {
      const response = await fetch(URLs.empLeaveDetailsAll, {
        headers: {
          Authorization: `Bearer ${hrms_token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      const formattedData = data.map((item) => ({
        empLeaveId: item.empLeaveId,
        employeeId: item.employeeRecordID,
        employeeName: item.employeeName,
        leaveId: item.leaveId,
        dayCount: item.dayCount,
        fromDate: new Date(item.fromDate).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
        toDate: new Date(item.toDate).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
        approvedStatus:
          item.approvedStatus?.trim() === "A"
            ? "Approved"
            : item.approvedStatus?.trim() === "R"
            ? "Rejected"
            : "Pending",
        remarks: item.remarks,
      }));
      setEmployeeData(formattedData);
    } catch (error) {
      console.error("Error fetching leave data:", error);
      showNotification("Failed to fetch leave data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveData();
  }, []);

  const handleOpenApprovalDialog = (employeeId, status, empLeaveId) => {
    const currentStatus = employeeData.find(
      (item) => item.empLeaveId === empLeaveId
    )?.approvedStatus;

    if (status === "Rejected" && currentStatus === "Approved") {
      setApprovalParams({ employeeId, status, empLeaveId });
      setOpenApprovalDialog(true);
    } else {
      handleApprovalStatus(employeeId, status, empLeaveId);
    }
  };

  const handleCloseApprovalDialog = () => {
    setOpenApprovalDialog(false);
    setApprovalParams({});
  };

  const handleApprovalStatus = async (employeeId, status, empLeaveId) => {
    try {
      const apiStatus = status === "Approved" ? "A" : status === "Rejected" ? "R" : "P";

      // Optimistically update UI
      setEmployeeData((prevData) =>
        prevData.map((item) =>
          item.empLeaveId === empLeaveId ? { ...item, approvedStatus: status } : item
        )
      );

      setLoading(true);
      const response = await fetch(URLs.hrmsUpdateLeaveUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
        body: JSON.stringify({
          employeeRecordId: employeeId,
          empLeaveId: empLeaveId,
          approvedStatus: apiStatus,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
      }

      showNotification(`Successfully ${status.toLowerCase()} the leave request`);
      await fetchLeaveData(); // Refresh data
    } catch (error) {
      console.error("Error updating approval status:", error);
      showNotification(
        `Failed to ${status.toLowerCase()} the leave request: ${error.message}`,
        "error"
      );
      await fetchLeaveData(); // Revert optimistic update
    } finally {
      setLoading(false);
      handleCloseApprovalDialog();
    }
  };

  const ApprovalStatusCellRenderer = (props) => {
    const status = props.data.approvedStatus;
    const statusColor = status === "Approved" ? "green" : status === "Rejected" ? "red" : "orange";

    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        {status === "Approved" && <CheckCircleIcon style={{ color: statusColor }} />}
        {status === "Rejected" && <CancelIcon style={{ color: statusColor }} />}
        {status === "Pending" && <span style={{ color: statusColor }}>●</span>}
        <span style={{ marginLeft: "8px", color: statusColor }}>{status}</span>
      </div>
    );
  };

  const ActionCellRenderer = (props) => {
    const { data } = props;
    const status = data.approvedStatus;

    return (
      <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
        {(status === "Pending" || status === "Rejected") && (
          <Tooltip title="Approve">
            <IconButton
              onClick={() =>
                handleApprovalStatus(props.data.employeeId, "Approved", props.data.empLeaveId)
              }
              color="primary"
              size="small"
            >
              <CheckCircleIcon />
            </IconButton>
          </Tooltip>
        )}

        {(status === "Pending" || status === "Approved") && (
          <Tooltip title="Reject">
            <IconButton
              onClick={() =>
                handleOpenApprovalDialog(props.data.employeeId, "Rejected", props.data.empLeaveId)
              }
              color="error"
              size="small"
            >
              <CancelIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>
    );
  };
  ActionCellRenderer.propTypes = {
    data: PropTypes.shape({
      approvedStatus: PropTypes.string.isRequired,
      employeeId: PropTypes.string.isRequired,
      empLeaveId: PropTypes.string.isRequired,
    }).isRequired,
  };
  const ViewRemarksRenderer = (props) => (
    <div style={{ cursor: "pointer", display: "flex", justifyContent: "center" }}>
      <Tooltip title="View Remarks">
        <IconButton
          onClick={() => {
            setSelectedRemarks(props.value);
            setRemarksDialogOpen(true);
          }}
          size="small"
        >
          <VisibilityIcon style={{ color: "#1a73e8" }} />
        </IconButton>
      </Tooltip>
    </div>
  );

  ViewRemarksRenderer.propTypes = {
    value: PropTypes.string,
  };

  ViewRemarksRenderer.defaultProps = {
    value: "",
  };

  const columnDefs = [
    {
      headerName: "Employee Name",
      field: "employeeName",
      sortable: true,
      filter: true,
      flex: 1.5,
    },
    {
      headerName: "Leave Type",
      field: "leaveId",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Days",
      field: "dayCount",
      sortable: true,
      filter: true,
      flex: 0.8,
    },
    {
      headerName: "From Date",
      field: "fromDate",
      sortable: true,
      filter: true,
      flex: 1,
      sort: "desc",
    },
    {
      headerName: "To Date",
      field: "toDate",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Remarks",
      field: "remarks",
      cellRenderer: ViewRemarksRenderer,
      width: 100,
      cellStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    },
    {
      headerName: "Status",
      field: "approvedStatus",
      cellRenderer: ApprovalStatusCellRenderer,
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: ActionCellRenderer,
      sortable: false,
      filter: false,
      width: 120,
    },
  ];

  return (
    <DashboardLayout>
      <StyledTableContainer>
        <StyledAgGridContainer component={Paper} className="ag-theme-alpine" sx={{ flexGrow: 1 }}>
          <AgGridReact
            rowData={employeeData}
            columnDefs={columnDefs}
            pagination={true}
            paginationPageSize={10}
            suppressCellFocus={true}
            defaultColDef={{
              resizable: true,
              filter: true,
            }}
            onGridReady={(params) => {
              params.api.sizeColumnsToFit();
            }}
            onFirstDataRendered={(params) => {
              params.api.sizeColumnsToFit();
            }}
          />
        </StyledAgGridContainer>
      </StyledTableContainer>
      <StyledSnackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <StyledAlert onClose={handleCloseSnackbar} severity={snackbarStatus} sx={{ width: "100%" }}>
          {snackbarMessage}
        </StyledAlert>
      </StyledSnackbar>
      <Dialog open={openApprovalDialog} onClose={handleCloseApprovalDialog}>
        <DialogContent>
          <DialogContentText sx={dialogContentTextStyle}>
            Are you sure you want to reject this previously approved leave?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={handleCloseApprovalDialog} color="primary">
            Cancel
          </StyledButton>
          <StyledButton
            onClick={() =>
              handleApprovalStatus(
                approvalParams.employeeId,
                approvalParams.status,
                approvalParams.empLeaveId
              )
            }
            color="error"
          >
            Confirm
          </StyledButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={loading}
        onClose={() => setLoading(false)}
        aria-labelledby="loading-dialog-title"
      >
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>
      <Dialog
        open={remarksDialogOpen}
        onClose={() => setRemarksDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Leave Remarks</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-wrap", mt: 2 }}>
            {selectedRemarks || "No remarks provided"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemarksDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

ApproveLeaves.propTypes = {
  data: PropTypes.shape({
    approvedStatus: PropTypes.string.isRequired,
    employeeId: PropTypes.string.isRequired,
  }),
};
